import React from "react";
import {
  AttachFile,
  DateRange,
  Description,
  DirectionsCarFilledOutlined,
  InfoOutlined,
  InsertChart,
  ListAltOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { localizeDateShort } from "utils/dateFormats";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";
import { useVehicleMaintenanceDetailContext } from "contexts/VehicleMaintenanceDetailContext";
import FileIcon from "components/Shared/FileIcon";

const VehicleMaintenanceBasicDetail = () => {
  const { vehicleMaintenanceDetail, loadingVehicleMaintenance } =
    useVehicleMaintenanceDetailContext();

  return !loadingVehicleMaintenance ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <InformationTile
            icon={() => <InsertChart color="primary" />}
            title="Maintenance ID"
            subtitle={vehicleMaintenanceDetail?.maintenanceId || "N/A"}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <DirectionsCarFilledOutlined color="primary" />}
            title="Vehicle"
            subtitle={
              vehicleMaintenanceDetail?.vehicleId?.registration_number || "N/A"
            }
          />
        </Grid>
        <Grid item xs={4}>
          <InformationTile
            icon={() => <DirectionsCarFilledOutlined color="primary" />}
            title="Merchant"
            subtitle={
              vehicleMaintenanceDetail?.merchant?.merchantName || "N/A"
            }
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <SettingsOutlined color="primary" />}
            title="Type"
            subtitle={vehicleMaintenanceDetail?.maintenanceType || "N/A"}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <DateRange color="primary" />}
            title="Date"
            subtitle={
              vehicleMaintenanceDetail?.maintenanceDate
                ? localizeDateShort(vehicleMaintenanceDetail.maintenanceDate)
                : "N/A"
            }
          />
        </Grid>

        {vehicleMaintenanceDetail?.nextMaintenanceDue && (
          <Grid item xs={4}>
            <InformationTile
              icon={() => <DateRange color="primary" />}
              title="Next Due"
              subtitle={localizeDateShort(
                vehicleMaintenanceDetail.nextMaintenanceDue
              )}
            />
          </Grid>
        )}

        <Grid item xs={4}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Condition"
            subtitle={vehicleMaintenanceDetail?.maintenanceCondition || "N/A"}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Maintenance Cost"
            subtitle={
              vehicleMaintenanceDetail?.cost
                ? `Rs. ${vehicleMaintenanceDetail.cost}.00`
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={4}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Status"
          >
            <Status isActive={vehicleMaintenanceDetail?.isActive ?? false} />
          </InformationTile>
        </Grid>
        <Grid item xs={4}>
          <InformationTile
            icon={() => <AttachFile color="primary" />}
            title="Attachments"
          >
            {vehicleMaintenanceDetail?.attachments && (
              <div className="flex gap-4">
                {vehicleMaintenanceDetail?.attachments?.map((attachment) => (
                  <a
                    key={attachment?._id}
                    href={attachment?.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--primary)" }}
                  >
                    <FileIcon
                      fileName={attachment?.name}
                      fileUrl={attachment?.url}
                    />
                  </a>
                ))}
              </div>
            )}
          </InformationTile>
        </Grid>

        <Grid item xs={12}>
          <InformationTile
            icon={() => <Description color="primary" />}
            title="Description"
            subtitle={vehicleMaintenanceDetail?.description || "N/A"}
          />
        </Grid>

        <Grid item xs={12}>
          <InformationTile
            icon={() => <ListAltOutlined color="primary" />}
            title="Parts Replaced"
            subtitle={
              <Box>
                {vehicleMaintenanceDetail?.partsReplaced?.length > 0 ? (
                  <Table size="small" aria-label="parts replaced">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="h7">Part Name</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h7">Part Cost</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vehicleMaintenanceDetail.partsReplaced.map(
                        (part, index) => (
                          <TableRow key={index}>
                            <TableCell>{part.partName || "N/A"}</TableCell>
                            <TableCell>
                              {part.partCost
                                ? `Rs. ${part.partCost}.00`
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography>No parts replaced</Typography>
                )}
                {vehicleMaintenanceDetail?.totalCost !== undefined && (
                  <Typography variant="h8" sx={{ mt: 2, fontWeight: "medium" }}>
                    Total Cost (with Initial Cost) : Rs.{" "}
                    {vehicleMaintenanceDetail.totalCost}.00
                  </Typography>
                )}
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default VehicleMaintenanceBasicDetail;
